import { useState, useRef, useEffect } from 'react';
import { Archive, RotateCcw, Paperclip, Send, Globe, FileText, PlusCircle, MoreHorizontal, ChevronDown, File, Image as ImageIcon, Volume2, Video, X } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { QUICK_REPLIES } from '../../data/mockData';
import { API_BASE_URL } from '../../api/client';

const PLATFORM_COLORS = {
  telegram: { bg: '#E3F4FC', color: '#0C6B96', dot: '#229ED9' },
  whatsapp: { bg: '#E1F5E9', color: '#1A6B36', dot: '#25D366' },
  wecom: { bg: '#E4F7EC', color: '#116B3A', dot: '#07C160' },
  teams: { bg: '#EDEBFF', color: '#3730A3', dot: '#6264A7' },
  email: { bg: '#FDECEA', color: '#9C1B14', dot: '#EA4335' },
  facebook: { bg: '#E7F0FD', color: '#0C4FA8', dot: '#1877F2' },
  instagram: { bg: '#FDEAF1', color: '#8B0D3A', dot: '#E1306C' },
};

const PLATFORM_NAMES = {
  telegram: 'Telegram', whatsapp: 'WhatsApp', wecom: '企业微信', teams: 'Teams', email: 'Email', facebook: 'Facebook', instagram: 'Instagram'
};

const MESSAGE_STATUS_LABELS = {
  pending: '发送中',
  sent: '已发送',
  failed: '发送失败',
};

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('tg://')) {
    const [, rest = ''] = url.split('tg://');
    const [, fileId = ''] = rest.split('/');
    return `${API_BASE_URL}/api/channel/telegram/files/${encodeURIComponent(fileId)}`;
  }
  if (/^https?:\/\//.test(url)) return url;
  if (url.startsWith('/api/') || url.startsWith('/v1/')) return `${API_BASE_URL}${url}`;
  return url;
};

const messageTypeForFile = (file) => {
  if (file.type?.startsWith('image/')) return 'image';
  if (file.type?.startsWith('video/')) return 'video';
  if (file.type?.startsWith('audio/')) return 'audio';
  return 'file';
};

function MessageContent({ msg, onPreviewImage }) {
  const mediaUrl = resolveMediaUrl(msg.mediaUrl);
  if (msg.type === 'image') {
    return (
      <div className="media-message">
        {mediaUrl ? (
          <button
            className="image-message-button"
            type="button"
            onClick={() => onPreviewImage({ url: mediaUrl, title: msg.text || '图片' })}
            aria-label="查看原图"
          >
            <img src={mediaUrl} alt={msg.text || 'image'} />
          </button>
        ) : <ImageIcon size={18} />}
        {msg.text && <span>{msg.text}</span>}
      </div>
    );
  }
  if (msg.type === 'audio') {
    return (
      <div className="media-message compact">
        <Volume2 size={16} />
        {mediaUrl ? <audio controls src={mediaUrl} /> : <span>{msg.text || '音频消息'}</span>}
      </div>
    );
  }
  if (msg.type === 'video') {
    return (
      <div className="media-message">
        {mediaUrl ? <video controls src={mediaUrl} /> : <Video size={18} />}
        {msg.text && <span>{msg.text}</span>}
      </div>
    );
  }
  if (msg.type === 'file') {
    return (
      <a className="file-message" href={mediaUrl || '#'} target="_blank" rel="noreferrer">
        <File size={16} />
        <span>{msg.text}</span>
      </a>
    );
  }
  if (msg.type === 'quote') {
    return <pre className="quote-message">{msg.text}</pre>;
  }
  return msg.text;
}

function Message({ msg, contact, onPreviewImage }) {
  const [expanded, setExpanded] = useState(false);
  const isIn = msg.from === 'contact';
  const { currentUser, translateMessage, translatingMsgId } = useStore();
  const translating = translatingMsgId === msg.id;

  const handleTranslate = async () => {
    if (msg.translated) {
      setExpanded(!expanded);
      return;
    }
    await translateMessage(msg.id, 'zh');
    setExpanded(true);
  };

  return (
    <div className={`msg-row ${isIn ? '' : 'out'}`}>
      {isIn ? (
        <div className="msg-av" style={{ background: contact.avatarBg, color: contact.avatarColor }}>
          {contact.initials}
        </div>
      ) : (
        <div className="msg-av" style={{ background: '#B5D4F4', color: '#0C447C' }}>
          {currentUser.initials}
        </div>
      )}

      <div className="msg-body">
        <div className={`bubble ${isIn ? 'bubble-in' : 'bubble-out'}`}>
          <MessageContent msg={msg} onPreviewImage={onPreviewImage} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: isIn ? 'flex-start' : 'flex-end' }}>
          <span className="bubble-time">{msg.time}</span>
          {!isIn && (
            <span className={`message-status ${msg.status === 'failed' ? 'failed' : ''}`}>
              {MESSAGE_STATUS_LABELS[msg.status] || '已发送'}
            </span>
          )}
        </div>

        {isIn && (
          <>
            <button className="translate-row" onClick={handleTranslate} disabled={translating}>
              <Globe size={11} />
              <span>{translating ? '翻译中' : msg.translated ? (expanded ? '收起译文' : '查看中文译文') : '翻译为中文'}</span>
              {msg.translated && <ChevronDown size={11} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
            </button>
            {expanded && msg.translated && (
              <div className="translate-bubble">{msg.translated}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function ChatArea() {
  const { getActiveConv, getActiveContact, sendMessage, sendQuote, translateText, uploadFile, showCrm, toggleCrm, openReminderComposer, closeConversation, reopenConversation, loadWeComState, wecomStates, wecomStateLoading } = useStore();
  const conv = getActiveConv();
  const contact = getActiveContact();
  const [inputText, setInputText] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [sendError, setSendError] = useState('');
  const [busyAction, setBusyAction] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const sendingMessageRef = useRef(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conv?.messages?.length]);

  useEffect(() => {
    if (!imagePreview) return undefined;
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setImagePreview(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [imagePreview]);

  useEffect(() => {
    setEmailSubject('');
    setSendError('');
    if (conv?.platform === 'wecom') {
      loadWeComState(conv.id);
    }
  }, [conv?.id]);

  if (!conv || !contact) return <div className="chat-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--c-text-3)' }}>请选择一个会话</div>;

  const platStyle = PLATFORM_COLORS[conv.platform] || PLATFORM_COLORS.telegram;
  const platName = PLATFORM_NAMES[conv.platform] || conv.platform;
  const isClosed = conv.status === 'closed';
  const isEmail = conv.platform === 'email';
  const wecomState = conv.platform === 'wecom' ? wecomStates[conv.id] : null;
  const canSendText = !isClosed && !isSendingMessage && inputText.trim().length > 0;

  const handleSend = async () => {
    if (isClosed || sendingMessageRef.current) return;
    const text = inputText.trim();
    if (!text) return;
    const payload = isEmail
      ? { text, subject: emailSubject.trim() || undefined }
      : text;
    sendingMessageRef.current = true;
    setIsSendingMessage(true);
    setSendError('');
    try {
      await sendMessage(payload);
      setInputText('');
      textareaRef.current?.focus();
    } catch (error) {
      setSendError(error.message || '发送失败，请稍后重试');
      textareaRef.current?.focus();
    } finally {
      sendingMessageRef.current = false;
      setIsSendingMessage(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSendText) {
        handleSend();
      }
    }
  };

  const handleQR = (text) => {
    setInputText(text);
    textareaRef.current?.focus();
  };

  const handleTranslateInput = async () => {
    const text = inputText.trim();
    if (!text) return;
    setBusyAction('translate');
    try {
      const translated = await translateText(text, 'en');
      setInputText(translated);
      textareaRef.current?.focus();
    } finally {
      setBusyAction(null);
    }
  };

  const handleSendQuote = async () => {
    if (isClosed) return;
    setBusyAction('quote');
    try {
      await sendQuote({
        title: `${contact.company || contact.name} product quotation`,
        amount: 'To be confirmed',
        validDays: 7,
      });
    } finally {
      setBusyAction(null);
    }
  };

  const handleAttachClick = () => {
    if (isClosed) return;
    fileInputRef.current?.click();
  };

  const handleCloseConversation = async () => {
    if (!window.confirm(`关闭与「${contact.name}」的当前会话？`)) return;
    await closeConversation(conv.id);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusyAction('upload');
    try {
      const uploaded = await uploadFile(file);
      await sendMessage({
        text: uploaded.originalName || file.name,
        type: messageTypeForFile(file),
        mediaUrl: uploaded.url,
      });
    } finally {
      setBusyAction(null);
      event.target.value = '';
    }
  };

  return (
    <div className="chat-area">
      {/* Header */}
      <div className="chat-header">
        <div className="contact-avatar" style={{ background: contact.avatarBg, color: contact.avatarColor, width: 38, height: 38, fontSize: 13, fontWeight: 600 }}>
          {contact.initials}
        </div>
        <div className="chat-header-info">
          <div className="chat-header-name">
            {contact.name}
            <span className="platform-pill" style={{ background: platStyle.bg, color: platStyle.color }}>
              {platName}
            </span>
            {conv.sourceScene && (
              <span className="source-scene-pill" title={`来源：${conv.sourceScene}`}>
                {conv.sourceScene}
              </span>
            )}
            {wecomState && (
              <span className={`wecom-state-pill ${wecomState.canSend ? 'ready' : 'blocked'}`} title={wecomState.reason}>
                {wecomState.label}
              </span>
            )}
            {conv.platform === 'wecom' && !wecomState && (
              <span className="wecom-state-pill loading">{wecomStateLoading ? '企微状态读取中' : '企微状态待刷新'}</span>
            )}
          </div>
          <div className="chat-header-sub">
            <span className="online-dot" />
            <span>在线</span>
            <span>·</span>
            <span>{contact.country}</span>
            <span>·</span>
            <span>{contact.company}</span>
          </div>
        </div>
        <div className="header-actions">
          <button className="icon-btn tooltip-wrap" title="AI翻译助手" onClick={handleTranslateInput} disabled={busyAction === 'translate' || isClosed}>
            <Globe size={15} />
            <span className="tooltip">{busyAction === 'translate' ? '翻译中' : 'AI翻译'}</span>
          </button>
          <button className="icon-btn tooltip-wrap" title="发送报价单" onClick={handleSendQuote} disabled={busyAction === 'quote' || isClosed}>
            <FileText size={15} />
            <span className="tooltip">{busyAction === 'quote' ? '发送中' : '发送报价单'}</span>
          </button>
          <button className="icon-btn tooltip-wrap" title="添加任务" onClick={openReminderComposer} disabled={isClosed}>
            <PlusCircle size={15} />
            <span className="tooltip">添加任务</span>
          </button>
          {isClosed ? (
            <button className="icon-btn tooltip-wrap" title="重新打开会话" onClick={() => reopenConversation(conv.id)}>
              <RotateCcw size={15} />
              <span className="tooltip">重新打开</span>
            </button>
          ) : (
            <button className="icon-btn tooltip-wrap" title="关闭会话" onClick={handleCloseConversation}>
              <Archive size={15} />
              <span className="tooltip">关闭会话</span>
            </button>
          )}
          <button
            className={`icon-btn tooltip-wrap ${showCrm ? 'active' : ''}`}
            onClick={toggleCrm}
          >
            <MoreHorizontal size={15} />
            <span className="tooltip">{showCrm ? '隐藏客户信息' : '显示客户信息'}</span>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="messages-area">
        {isClosed && <div className="closed-banner">会话已关闭，只能查看历史消息。重新打开后可继续发送。</div>}
        <div className="date-divider">今天</div>
        {conv.messages.map(msg => (
          <Message key={msg.id} msg={msg} contact={contact} onPreviewImage={setImagePreview} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {imagePreview && (
        <div className="image-preview-overlay" role="dialog" aria-modal="true" aria-label="图片预览" onClick={() => setImagePreview(null)}>
          <div className="image-preview-shell" onClick={event => event.stopPropagation()}>
            <div className="image-preview-toolbar">
              <span>{imagePreview.title}</span>
              <a href={imagePreview.url} target="_blank" rel="noreferrer">打开原图</a>
              <button type="button" onClick={() => setImagePreview(null)} aria-label="关闭图片预览">
                <X size={18} />
              </button>
            </div>
            <img src={imagePreview.url} alt={imagePreview.title} />
          </div>
        </div>
      )}

      {/* Quick Replies */}
      <div className="quick-replies-bar">
        <span className="qr-label">快捷：</span>
        {QUICK_REPLIES.map(qr => (
          <button key={qr.id} className="qr-chip" onClick={() => handleQR(qr.text)}>
            {qr.label}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="input-area">
        {sendError && (
          <div className="send-error" role="alert">
            {sendError}
          </div>
        )}
        <button className="attach-btn" onClick={handleAttachClick} title={process.env.REACT_APP_ATTACHMENTS_ENABLED === 'false' ? '附件功能暂未启用' : isEmail ? '邮件附件暂未启用' : '发送附件'} disabled={process.env.REACT_APP_ATTACHMENTS_ENABLED === 'false' || busyAction === 'upload' || isClosed || isEmail}>
          <Paperclip size={15} />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <div className="input-fields">
          {isEmail && (
            <input
              className="email-subject-input"
              type="text"
              placeholder="邮件主题（留空则使用发信账号默认主题）"
              value={emailSubject}
              onChange={e => setEmailSubject(e.target.value)}
              disabled={isClosed || isSendingMessage}
            />
          )}
          <textarea
            ref={textareaRef}
            className="msg-textarea"
            placeholder={isEmail ? '输入邮件正文... (Enter 发送，Shift+Enter 换行)' : '输入消息... (Enter 发送，Shift+Enter 换行，支持中文AI辅助翻译)'}
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isClosed || isSendingMessage}
            rows={1}
          />
        </div>
        <button className="send-btn" onClick={handleSend} disabled={!canSendText} title={isSendingMessage ? '发送中' : '发送'}>
          <Send size={15} color="white" />
        </button>
      </div>
    </div>
  );
}
